import { Box, Button, TextField, Typography, Link } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    createSearchParams,
    Link as RouterLink,
    Outlet,
    useNavigate,
} from 'react-router';
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile';
import { TurnstileAction } from 'dg-web-shared/lib/TurnstileActions';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { ParkingPortalFooter } from '../layout/ParkingPortalFooter.tsx';
import { ParkingPortalLogoHeader } from '../layout/ParkingPortalLogoHeader.tsx';
import * as Flux from 'dg-web-shared/lib/Flux.tsx';
import { useStore } from 'dg-web-shared/lib/Flux.tsx';
import * as AuthenticationState from '../../app/state/AuthenticationState.ts';
import { LoginState } from '../../landing-page/LandingPageState.ts';
import * as AuthenticationActions from '../../app/actions/AuthenticationActions.ts';
import {
    devEnvs,
    getDevEnvName,
    getEnvChangeName,
} from 'dg-web-shared/common/utils/DevEnvironments.tsx';
import {
    envIsProduction,
    envIsTest,
    envName,
} from 'dg-web-shared/lib/Environment.ts';
import { useEffect, useRef, useState } from 'react';
import { Language, languageFromString } from 'dg-web-shared/lib/Text.ts';
import * as SettingsState from '../../common/state/SettingsState.ts';

function setUsername(store: Flux.Store, username: string) {
    LoginState.stateWrite(store, {
        username: username,
        showCredentialsError: false,
        showTurnstileError: false,
    });
    return 'setUsername';
}

function setPassword(store: Flux.Store, password: string) {
    LoginState.stateWrite(store, {
        password: password,
        showCredentialsError: false,
        showTurnstileError: false,
    });
    return 'setPassword';
}

const PARKINGPORTAL_TURNSTILE_SITE_KEY = '0x4AAAAAABatiRYriPvAMuT9';

export function MobileLoginPage() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { storeState, update } = useStore(s => ({
        authentication: new AuthenticationState.StateSlice(s).state,
        settings: new SettingsState.StateSlice(s).state,
        login: LoginState.get(s),
    }));
    const turnstileRef = useRef<TurnstileInstance | null>(null);
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
    const showLoginError =
        storeState.login.showCredentialsError ||
        storeState.login.showTurnstileError;

    const envChangeName = getEnvChangeName(storeState.login.username);
    useEffect(() => {
        if (!envChangeName) return;
        if (envChangeName === 'test' && !envIsTest()) {
            window.location.href =
                'https://operator.test.digitalparking.ch/mobile/login/';
        } else if (
            devEnvs.includes(envChangeName) &&
            envName() !== envChangeName
        ) {
            window.location.href = `https://operator.${envChangeName}.digitalparking.dev/mobile/login`;
        } else if (envChangeName === 'prod' && !envIsProduction()) {
            window.location.href = 'https://parkingportal.ch/mobile/login';
        }
    }, [envChangeName]);
    const devEnvName = getDevEnvName();

    useEffect(() => {
        if (!showLoginError) {
            return;
        }

        setTurnstileToken(null);
        turnstileRef.current?.reset();
    }, [showLoginError]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: '100%',
                overflowY: 'auto',
            }}
        >
            <ParkingPortalLogoHeader />
            <Box
                sx={{
                    padding: 3,
                }}
            >
                <TextField
                    label={
                        <Localized
                            de="Benutzername"
                            fr="Nom utilisateur"
                            it="Nome utente"
                            en="Username"
                        />
                    }
                    value={storeState.login.username}
                    onChange={v =>
                        update(store => setUsername(store, v.target.value))
                    }
                    fullWidth={true}
                    margin="normal"
                />
                <TextField
                    label={
                        <Localized
                            de="Passwort"
                            fr="Mot de passe"
                            it="Password"
                            en="Password"
                        />
                    }
                    value={storeState.login.password}
                    onChange={v =>
                        update(store => setPassword(store, v.target.value))
                    }
                    fullWidth={true}
                    type="password"
                    error={storeState.login.showCredentialsError}
                    helperText={
                        storeState.login.showCredentialsError ? (
                            <Localized
                                de="Benutzername oder Passwort sind ungültig."
                                fr="Le nom d'utilisateur ou le mot de passe ne sont valables."
                                it="Nome utente o password non validi."
                                en="Invalid username or password."
                            />
                        ) : undefined
                    }
                    autoComplete="current-password"
                />
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                        gap: theme.spacing(2),
                        marginTop: theme.spacing(2),
                        marginBottom: theme.spacing(5),
                    }}
                >
                    <Box sx={{ width: '100%', maxWidth: 320, mt: 1 }}>
                        <Turnstile
                            ref={turnstileRef}
                            siteKey={PARKINGPORTAL_TURNSTILE_SITE_KEY}
                            options={{
                                theme: 'light',
                                size: 'flexible',
                                action: TurnstileAction.OPERATOR_LOGIN,
                            }}
                            onError={() => setTurnstileToken(null)}
                            onExpire={() => setTurnstileToken(null)}
                            onSuccess={token => setTurnstileToken(token)}
                        />
                    </Box>
                    {storeState.login.showTurnstileError && (
                        <Typography
                            component="div"
                            color="error"
                            sx={{ textAlign: 'center' }}
                        >
                            <Localized
                                de="Bitte bestätigen Sie, dass Sie kein Roboter sind."
                                fr="Veuillez confirmer que vous n'êtes pas un robot."
                                it="Confermate di non essere un robot."
                                en="Please confirm that you are not a robot."
                            />
                        </Typography>
                    )}
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={
                            !storeState.login.username ||
                            !storeState.login.password ||
                            storeState.login.showCredentialsError ||
                            turnstileToken == null
                        }
                        loading={storeState.authentication.statusPending}
                        onClick={() =>
                            update(AuthenticationActions.login(navigate), {
                                username:
                                    storeState.login.username?.trim() || '',
                                password: storeState.login.password || '',
                                longSession: storeState.login.longSession,
                                turnstileToken,
                            })
                        }
                    >
                        <Localized
                            de={`Anmelden${devEnvName}`}
                            fr={`Connexion${devEnvName}`}
                            it={`Accesso${devEnvName}`}
                            en={`Login${devEnvName}`}
                        />
                    </Button>
                </Box>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                }}
            >
                <Link
                    component={RouterLink}
                    sx={{
                        fontWeight: 700,
                    }}
                    underline="always"
                    to={resetPasswordLink(
                        languageFromString(storeState.settings.language),
                        storeState.login.username || '',
                    )}
                >
                    <Localized
                        de="Passwort vergessen?"
                        fr="Mot de passe oublié?"
                        it="Password dimenticata?"
                        en="Forgot password?"
                    />
                </Link>
            </Box>
            <ParkingPortalFooter />
            <Outlet />
        </Box>
    );
}

function resetPasswordLink(language: Language, username: string) {
    return {
        pathname: '/mobile/reset-password',
        search: createSearchParams({ username, language }).toString(),
    };
}
